import { useCallback, useEffect, useState } from 'react'
import { Printer } from 'phosphor-react'
import { ContentLoading } from '../../../../components/ContentLoading'
import apinode from '../../../../services/apinode'
import { sendErrorApiMessage } from '../../../../utils/sendErrorApiMessage'
import Navbar from '../../../../components/navbar/navbar'
import { useOrdenation } from '../../../../hooks/useOrdenation'
import { gerarRelatorioPDF } from './gerarRelatorioPDF'

// ─── Status badge ─────────────────────────────────────────────────────────────

const statusColors = {
  'ARMAZENADO COM FALTA': { bg: '#fee2e2', color: '#991b1b' },
  'AGUARDANDO SEPARAÇÃO': { bg: '#fef3c7', color: '#92400e' },
  'ARMAZENADO COMPLETO': { bg: '#d1fae5', color: '#065f46' },
  ENTREGUE: { bg: '#ede9fe', color: '#5b21b6' },
}

const StatusBadge = ({ label }) => {
  const style = statusColors[label] || { bg: '#f0ede8', color: '#444' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fieldLabel = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.8px',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: 7,
}

const fieldInput = {
  width: '100%',
  padding: '9px 14px',
  border: '1px solid #ddd',
  borderRadius: 8,
  fontSize: 13,
  color: '#1a1a1a',
  background: '#fafaf8',
  outline: 'none',
  boxSizing: 'border-box',
}

const fieldSelect = {
  ...fieldInput,
  cursor: 'pointer',
}

const emptyFiltros = {
  pedido: '',
  cliente: '',
  fornecedor: '',
  sku: '',
  status: '',
  dataInicio: '',
  dataFim: '',
  cidade: '',
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LojaRelatorio() {
  const [isFetchLoading, setIsFetchLoading] = useState(false)
  const [pedidosList, setPedidosList] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [fornecedoresOptions, setFornecedoresOptions] = useState([])
  const [filtros, setFiltros] = useState(emptyFiltros)
  const [hasSearched, setHasSearched] = useState(false)

  const { filteredItems, Th, Paginate } = useOrdenation(
    pedidosList,
    'data_pedido',
    'desc',
  )

  const fetchStatusOptions = useCallback(async () => {
    try {
      const response = await apinode.get(
        `/loja/status/${localStorage.idconta}?tipo=S`,
      )
      setStatusOptions(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar status')
    }
  }, [])

  const fetchFornecedoresOptions = useCallback(async () => {
    try {
      const response = await apinode.get(
        `/loja/fornecedores/${localStorage.idconta}`,
      )
      setFornecedoresOptions(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao buscar fornecedores')
    }
  }, [])

  useEffect(() => {
    fetchStatusOptions()
    fetchFornecedoresOptions()
  }, [fetchStatusOptions, fetchFornecedoresOptions])

  const handleFiltrar = async () => {
    try {
      setIsFetchLoading(true)
      setHasSearched(true)

      const params = new URLSearchParams()
      if (filtros.pedido) params.append('pedido', filtros.pedido)
      if (filtros.cliente) params.append('cliente', filtros.cliente)
      if (filtros.fornecedor) params.append('fornecedor', filtros.fornecedor)
      if (filtros.sku) params.append('sku', filtros.sku)
      if (filtros.status) params.append('status', filtros.status)
      if (filtros.dataInicio) params.append('data_ini', filtros.dataInicio)
      if (filtros.dataFim) params.append('data_fim', filtros.dataFim)
      if (filtros.cidade) params.append('cidade', filtros.cidade)

      const response = await apinode.get(
        `/loja/relatorio/${localStorage.idconta}?${params.toString()}`,
      )

      setPedidosList(response.data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao filtrar pedidos')
    } finally {
      setIsFetchLoading(false)
    }
  }

  const handleImprimir = () => {
    gerarRelatorioPDF(filteredItems)
  }

  return (
    <>
      <Navbar disableExtraDiv />

      <div
        className="mt-page"
        style={{
          display: 'flex',
          minHeight: '100vh',
          background: '#f5f4f0',
          fontSize: 14,
          flex: 1,
        }}
      >
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 28, flex: 1 }}>
            {/* Page header */}
            <div
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: '#1a1a1a',
                marginBottom: 4,
              }}
            >
              Relatório de Pedidos
            </div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
              Filtre e imprima os pedidos em A4.
            </div>

            {/* Filtros */}
            <div
              style={{
                background: '#fff',
                border: '1px solid #e8e6e0',
                borderRadius: 10,
                padding: '20px 20px 16px',
                marginBottom: 24,
              }}
            >
              {/* Row 1 */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4,1fr)',
                  gap: 16,
                  marginBottom: 16,
                }}
              >
                <div>
                  <div style={fieldLabel}>Nº Pedido</div>
                  <input
                    type="text"
                    placeholder="Ped. Loja ou Matriz..."
                    value={filtros.pedido}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, pedido: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>
                <div>
                  <div style={fieldLabel}>Cliente</div>
                  <input
                    type="text"
                    placeholder="Nome do cliente..."
                    value={filtros.cliente}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, cliente: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>
                <div>
                  <div style={fieldLabel}>Fornecedor</div>
                  <select
                    value={filtros.fornecedor}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, fornecedor: e.target.value }))
                    }
                    style={fieldInput}
                  >
                    <option value="">Todos</option>
                    {fornecedoresOptions.map((s) => (
                      <option
                        key={s.id}
                        value={s.id}
                      >
                        {s.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabel}>Produto</div>
                  <input
                    type="text"
                    placeholder="SKU..."
                    value={filtros.sku}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, sku: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div
                style={{
                  display: 'grid',
                  gap: 16,
                  gridTemplateColumns: 'repeat(5,1fr)',
                }}
              >
                <div>
                  <div style={fieldLabel}>Status</div>
                  <select
                    value={filtros.status}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, status: e.target.value }))
                    }
                    style={fieldSelect}
                  >
                    <option value="">Todos</option>
                    {statusOptions.map((s) => (
                      <option
                        key={s.id}
                        value={s.id}
                      >
                        {s.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={fieldLabel}>Data Início</div>
                  <input
                    type="date"
                    value={filtros.dataInicio}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, dataInicio: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>
                <div>
                  <div style={fieldLabel}>Data Fim</div>
                  <input
                    type="date"
                    value={filtros.dataFim}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, dataFim: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>

                <div style={{ width: 200 }}>
                  <div style={fieldLabel}>Cidade</div>
                  <input
                    type="text"
                    placeholder="Cidade..."
                    value={filtros.cidade}
                    onChange={(e) =>
                      setFiltros((f) => ({ ...f, cidade: e.target.value }))
                    }
                    style={fieldInput}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-end',
                  }}
                >
                  <button
                    onClick={handleFiltrar}
                    style={{
                      margin: 0,
                      background: '#2d2d2d',
                      color: '#fff',
                      border: 'none',
                      padding: '9px 22px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Filtrar
                  </button>

                  <button
                    onClick={handleImprimir}
                    disabled={filteredItems.length === 0}
                    style={{
                      margin: 0,
                      background:
                        filteredItems.length === 0 ? '#e8e6e0' : '#c8a030',
                      color: filteredItems.length === 0 ? '#aaa' : '#fff',
                      border: 'none',
                      padding: '9px 20px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor:
                        filteredItems.length === 0 ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                    }}
                  >
                    <Printer size={15} />
                    Imprimir A4
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <ContentLoading
              isLoading={isFetchLoading}
              loadingText="Filtrando pedidos..."
            >
              {hasSearched && (
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 10,
                    border: '1px solid #e8e6e0',
                    overflow: 'hidden',
                  }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8f7f3' }}>
                        {[
                          { label: 'Ped. Loja', value: 'idpedidoloja' },
                          { label: 'Ped. Matriz', value: 'pedido_matriz' },
                          { label: 'Cliente', value: 'cliente' },
                          { laebel: 'Data', value: 'data_pedido' },
                          { label: 'Entrega', value: 'data_entrega' },
                          { label: 'Armazenado', value: 'data_armazenado' },
                          { label: 'Cidade', value: 'cidade' },
                          { label: 'Status', value: 'status_nome' },
                        ].map((h) => (
                          <Th
                            key={h.value}
                            label={h.label}
                            value={h.value}
                            style={{
                              padding: '11px 14px',
                              textAlign: 'left',
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#999',
                              letterSpacing: '0.8px',
                              textTransform: 'uppercase',
                              borderBottom: '1px solid #e8e6e0',
                              whiteSpace: 'nowrap',
                              background: '#f8f7f3',
                            }}
                          >
                            {h}
                          </Th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item, index) => (
                        <tr
                          key={item.id}
                          style={{
                            borderBottom:
                              index < filteredItems.length - 1
                                ? '1px solid #f0ede8'
                                : 'none',
                          }}
                        >
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#2a2a2a',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {item.idpedidoloja}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#555',
                              fontSize: 13,
                            }}
                          >
                            {item.pedido_matriz ?? '—'}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#2a2a2a',
                              fontSize: 13,
                              maxWidth: 240,
                            }}
                          >
                            {item.cliente}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#555',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.data_pedido
                              ? new Date(item.data_pedido).toLocaleDateString(
                                  'pt-BR',
                                )
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#555',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.data_entrega
                              ? new Date(item.data_entrega).toLocaleDateString(
                                  'pt-BR',
                                )
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#555',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.data_armazenado
                              ? new Date(
                                  item.data_armazenado,
                                ).toLocaleDateString('pt-BR')
                              : '—'}
                          </td>
                          <td
                            style={{
                              padding: '13px 14px',
                              color: '#555',
                              fontSize: 13,
                            }}
                          >
                            {item.cidade ?? '—'}
                          </td>
                          <td style={{ padding: '13px 14px' }}>
                            {item.status_nome ? (
                              <StatusBadge label={item.status_nome} />
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                      {filteredItems.length === 0 && (
                        <tr>
                          <td
                            colSpan={9}
                            style={{
                              padding: '32px 20px',
                              textAlign: 'center',
                              color: '#aaa',
                              fontSize: 13,
                            }}
                          >
                            Nenhum pedido encontrado para os filtros informados.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <Paginate />
                </div>
              )}
            </ContentLoading>
          </div>
        </div>
      </div>
    </>
  )
}
